import { invariantResponse } from '@epic-web/invariant'
import { Character, Prisma } from "@prisma/client";
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Form, Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { getServerAndShardList } from '#app/utils/shards.server.js'
import { useOptionalUser } from '#app/utils/user.ts'

type Server = {
	id: string
	name: string
	slug: string
	logo: string
	website: string
	description: string
	establishedAt: Date | null
	createdAt: Date
}

type Shard = {
	id: string
	name: string
	slug: string
	server: Server[]
}

type UserShard = {
	id: string
	userId: string
	shardId: string | null
	shard: {
		id: string
		name: string
		slug: string
		server: Server[]
	} | null
	createdAt: Date
	details: string | null
	yearStart: number | null
	yearEnd: number | null
}

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findUnique({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			createdAt: true,
			description: true,
			shards: {
				select: {
					id: true,
					shard: {
						select: {
							id: true,
							name: true,
							slug: true,
							server: {
								select: {
									id: true,
									name: true,
									slug: true,
									logo: true,
									website: true,
									description: true,
									establishedAt: true,
									createdAt: true,
								},
							},
						},
					},
				},
			},
			characters: true,
			guilds: true,
			connections: {
				select: {
					providerName: true,
					providerId: true,
				},
			},
		},
		where: { username: params.username },
	})

	invariantResponse(user, 'User not found', { status: 404 })

	
	const result = await prisma.$queryRaw<
		{ userNotesCount: BigInt; userScriptsCount: BigInt; userReactinsCount: BigInt; userShardsCount: BigInt; userCharactersCount: BigInt; userBuildsCount: BigInt;  }[]
		>(
		Prisma.sql`
			SELECT
			(SELECT COUNT(*) FROM Note WHERE ownerId = ${user.id}) AS userNotesCount,
			(SELECT COUNT(*) FROM Script WHERE creatorId = ${user.id}) AS userScriptsCount,
			(SELECT COUNT(*) FROM Reaction WHERE userId = ${user.id}) AS userReactinsCount,
			(SELECT COUNT(*) FROM UserShard WHERE userId = ${user.id}) AS userShardsCount,
			(SELECT COUNT(*) FROM Character WHERE userId = ${user.id}) AS userCharactersCount,
			(SELECT COUNT(*) FROM UserBuildTemplate WHERE userId = ${user.id}) AS userBuildsCount
		`
	);



	const counts = result && result.length
	? {
		userNotesCount: Number(result[0]?.userNotesCount),
		userScriptsCount: Number(result[0]?.userScriptsCount),
		userReactinsCount: Number(result[0]?.userReactinsCount),
		userShardsCount: Number(result[0]?.userShardsCount),
		userCharactersCount: Number(result[0]?.userCharactersCount),
		userBuildsCount: Number(result[0]?.userBuildsCount),
		}
	: { userNotesCount: 0, userScriptsCount: 0, userReactinsCount: 0, userShardsCount: 0, userCharactersCount: 0, userBuildsCount: 0 };

	const { userNotesCount, userScriptsCount, userReactinsCount, userShardsCount, userCharactersCount, userBuildsCount } = counts;

	const userShards: UserShard[] = user.shards.map((shard) => ({
		id: shard.id,
		userId: user.id,
		shardId: shard.shard?.id ?? null,
		shard: shard.shard,
		createdAt: new Date(),
		details: null,
		yearStart: null,
		yearEnd: null,
	}))

	const shardsByString = userShards
		.map((shard) => `${shard.shard?.server[0]?.name ?? ''} - ${shard.shard?.name ?? ''}`)
		.join(', ')

	function createNameArrayStringWithCounts(characters: Array<{ name: string }>) {
		const nameCounts = characters.reduce((acc: { [key: string]: number }, { name }) => {
			acc[name] = (acc[name] || 0) + 1
			return acc
		}, {})

		return Object.entries(nameCounts).map(([name, count]) => `${name} (${count})`)
	}


	// Filter out specific connections - only look at twitch atm
	user.connections = user.connections.filter((connection) => connection.providerName === 'twitch')

	const userDetails = {
		aboutMe: user.description,
		counts: {
			notes: userNotesCount,
			scripts: userScriptsCount,
			reactions: userReactinsCount,
			shards: userShardsCount,
			characters: userCharactersCount,
			builds: userBuildsCount,
		},
		shards: user.shards,
		userShards,
		shardsAsString: shardsByString.length ? shardsByString : 'Not provided',
		characterNames: user.characters,
		characterNamesAsString: createNameArrayStringWithCounts(user.characters),
		guilds: user.guilds,
		questions: [],
		answers: [],
		notes: [],
		scripts: [],
		userReactions: [],
		connections: user.connections,
	}

	

	return json({ user, userDetails, userJoinedDisplay: user.createdAt.toLocaleDateString() })
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = data.user.id === loggedInUser?.id

	return (
		<div className="container mb-48 mt-36 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="container flex flex-col items-center rounded-3xl bg-muted p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<img
								src={getUserImgSrc(data.user.image?.id)}
								alt={userDisplayName}
								className="h-52 w-52 rounded-full object-cover"
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex flex-col items-center">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<h1 className="text-center text-h2">{userDisplayName}</h1>
					</div>
					{data.userDetails.connections.length > 0 && (
						<div className="mt-4 flex flex-col items-center gap-2">
							<div className="flex gap-4">
								Verified on:
								{data.userDetails.connections.map((connection) => {
									if (connection.providerName === 'twitch') {
										return (
											<a
												key={connection.providerName}
												href={`https://twitch.tv/${connection.providerId}`}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
											>
												<img src="/images/twitch.png" alt="Twitch" className="w-4 h-4" />
												<span>{connection.providerId}</span>
											</a>
										)
									}
									return null
								})}
							</div>
						</div>
					)}
					<p className="mt-2 text-center text-muted-foreground">
						Joined: {data.userJoinedDisplay}
					</p>
					<div className='flex flex-row'>
						<div className="m-8 text-center text-muted-foreground flex flex-col">
							Servers I play on: 
							{data.userDetails.userShards.map((shard) => (
								<div key={shard.id} className="flex">
									<Link
										to={`/servers/${shard.shard?.server[0]?.slug}/shards/${shard.shard?.slug}/players`}
										className='underline hover:no-underline'
									>
										{shard.shard?.server[0]?.name} - {shard.shard?.name}
									</Link>
								</div>
							))}
						</div>
						<div className="m-8 text-center text-muted-foreground flex flex-col">
							Character names I go by: 
							{data.userDetails.characterNamesAsString.map((character: string, index: number) => (
								<div key={`character_${index}`} className="flex">
									<Link
										to={`characters`}
										className='underline hover:no-underline'
									>
										{character}
									</Link>
								</div>
							))}
						</div>
					</div>
					<p className="mt-2 text-center text-muted-foreground">
						About me: {data.userDetails.aboutMe ?? 'Not provided'}
					</p>

					

					{isLoggedInUser ? (
						<Form action="/logout" method="POST" className="mt-3">
							<Button type="submit" variant="link" size="pill">
								<Icon name="exit" className="scale-125 max-md:scale-150">
									Logout
								</Icon>
							</Button>
						</Form>
					) : null}
					<div className="mt-10 flex gap-4">
						{isLoggedInUser ? (
							<>
								<Button asChild>
									<Link to="scripts">
										My scripts {data.userDetails.counts.scripts}
									</Link>
								</Button>
								<Button asChild>
									<Link to="notes">
										My notes {data.userDetails.counts.notes}
									</Link>
								</Button>
								<Button asChild>
									<Link to="reactions">
										My reactions {data.userDetails.counts.reactions}
									</Link>
								</Button>
								<Button asChild>
									<Link to="builds">
										My builds {data.userDetails.counts.builds}
									</Link>
								</Button>
								<Button asChild>
									<Link to="shards">
										My shards {data.userDetails.counts.shards}
									</Link>
								</Button>
								<Button asChild>
									<Link to="characters">
										My characters {data.userDetails.counts.characters}
									</Link>
								</Button>
								<Button asChild>
									<Link to="/settings/profile">
										Edit profile
									</Link>
								</Button>
							</>
						) : (
							<>
								<Button asChild>
									<Link to="scripts">
										{data.userDetails.counts.scripts} script{data.userDetails.counts.scripts != 1 ? "s" : ""}
									</Link>
								</Button>
								<Button asChild>
									<Link to="notes">
										{data.userDetails.counts.notes} note{data.userDetails.counts.notes != 1 ? "s" : ""}
									</Link>
								</Button>
								<Button asChild>
									<Link to="reactions">
										{data.userDetails.counts.reactions} reaction{data.userDetails.counts.reactions != 1 ? "s" : ""}
									</Link>
								</Button>
								<Button asChild>
									<Link to="builds">
										{data.userDetails.counts.builds} build{data.userDetails.counts.builds != 1 ? "s" : ""}
									</Link>
								</Button>
								<Button asChild>
									<Link to="shards">
										{data.userDetails.counts.shards} shard{data.userDetails.counts.shards != 1 ? "s" : ""}
									</Link>
								</Button>
								<Button asChild>
									<Link to="characters">
										{data.userDetails.counts.characters} character{data.userDetails.counts.characters != 1 ? "s" : ""}
									</Link>
								</Button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Profile of ${displayName} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
